import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { displayDateTime } from 'reducers/TimeReducer';
import { getAddressParties } from 'reducers/AddressReducer';
import { showDisplayDataWindow } from 'reducers/DialogsReducer';
import { handleError } from 'reducers/ErrorReducer';
import PrintsSearch from 'Search/components/PrintsSearch';
import { XGrid } from '@material-ui/x-grid';
import Tooltip from 'components/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import CheckIcon from '@material-ui/icons/Check';
import { getPersonWarrant } from 'reducers/WarrantReducer';
import AddIcon from '@material-ui/icons/Add';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Fab,
  IconButton,
  Button,
} from '@material-ui/core';
import PersonLookup2 from 'components/PersonLookup2';
import TextField2 from 'components/TextField2';
import Dictionary from 'components/Dictionary';
import formStyles, { useError, gridStyle, Row, RowInner, ColCard } from 'utils/formStyles';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import PlaceLookup from 'components/PlaceLookup';
import { saveAddressParty, delAddressParty } from 'reducers/AddressReducer';
import { getPlaceDetails } from 'reducers/EventsReducer';
import { getService } from 'reducers/service';

const bool = (params) => (params.value ? <CheckIcon /> : <span></span>);

const getPartyName = (row) => {
  const { PartyType, PlaceName, PersonName } = row;
  switch (PartyType) {
    case 'Place':
      return PlaceName;
    case 'Person':
      return PersonName;
    default:
      return '';
  }
};

const getPartyID = (row) => {
  const { PartyType, PlaceID, PersonID } = row;
  switch (PartyType?.toLowerCase()) {
    case 'place':
      return PlaceID;
    case 'person':
      return PersonID;
    default:
      return '';
  }
};

const renderWarrant = (params) => {
  const dispatch = useDispatch();
  const { ActiveWarrant } = params.row;
  if (!ActiveWarrant) return '';

  const onClick = async (ev) => {
    ev.stopPropagation();
    try {
      const ptsPersonID = params.row.ptsPartyID;
      const result = await getPersonWarrant(ptsPersonID);
      const warrants = result.data;
      dispatch(
        showDisplayDataWindow({
          data: processVarrants(warrants),
          title: `Active Warrants for ${params.row.Name}`,
        })
      );
    } catch (err) {
      props.handleError(err, 'Error, Cannot display events');
    }
  };

  const processVarrants = (warrants) => {
    warrants.forEach((warrant) => {
      delete warrant.ptsPersonID;
      delete warrant.ptsStatuteID;
      delete warrant.ptsWarrantID;
      delete warrant.ptsIncidentID;
    });
    return warrants;
  };

  return (
    <IconButton size="small" onClick={onClick}>
      <InfoIcon style={{ color: '#fff' }} />
    </IconButton>
  );
};

const columns = [
  { field: 'Name', headerName: 'Name', width: 150 },
  { field: 'PartyID', headerName: 'Party ID', width: 150 },
  { field: 'PartyType', headerName: 'Party Type', width: 150 },
  { field: 'PriorityOrder', headerName: 'Priority Order', width: 150 },
  { field: 'AdvisoryText', headerName: 'Advisory Text', width: 150 },
  { field: 'Relationship', headerName: 'Relationship', width: 150 },
  { field: 'ActiveWarrant', headerName: 'Active Warrant', width: 150, renderCell: renderWarrant },
  {
    field: 'Created',
    headerName: 'Created',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 120 },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 120 },
  {
    field: 'IsDeleted',
    headerName: 'Is Deleted',
    width: 120,
    format: 'bool',
    renderCell: bool,
  },
  { field: 'DeletedBy', headerName: 'Deleted By', width: 120 },
  { field: 'DeletedIP', headerName: 'Deleted IP', width: 120 },
];

const useStyles = makeStyles((theme) => ({
  ...formStyles,
  item: gridStyle(150, 400),
  wrap: {
    padding: theme.spacing(3),
  },
  col1: {
    ...gridStyle(350, '100%'),
  },
  col2: {
    ...gridStyle(250, '100%'),
  },
  editActions: {
    textAlign: 'right',
    paddingBottom: 8,
    '& hr': {
      margin: '8px 0',
    },
    '& button': {
      marginLeft: 8,
    },
    '& svg': {
      marginRight: 5,
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  warrant: {
    background: theme.palette.error.main,
    color: theme.colors.errorContrast,
    '&:hover': {
      background: `${theme.palette.error.main} !important`,
      color: theme.colors.errorContrast,
    },
    '&.Mui-selected': {
      background: `${theme.palette.error.main} !important`,
      color: theme.colors.errorContrast,
    },
  },
}));

function AddressParties(props) {
  const { ptsAddressID, dictionary } = props;
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selection, setSelection] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [edited, setEdited] = useState(false);
  const [PartyType, SetPartyType] = useState('Person');
  const [person, setPerson] = useState(null);
  const [place, setPlace] = useState(null);
  const [PriorityOrder, setPriorityOrder] = useState(0);
  const [PartyRelationship, setPartyRelationship] = useState(null);
  const [errors, setErrors] = useState();
  const [AdvisoryText, setAdvisoryText] = useState('');
  const [ROWGUID, setROWGUID] = useState();
  const [ptsPersonID, setPtsPersonID] = useState();
  const [editedPlace, setEditedPlace] = useState();
  const mountedRef = useRef(true);
  const isEditing = useRef(false);
  const { err, delErr, clearErr, isValidAndChanged, formChanged } = useError(errors, setErrors);

  useEffect(() => {
    getData();
    return () => (mountedRef.current = false);
    // eslint-disable-next-line
  }, []);
  // useEffect(() => {
  //   if (!isEditing.current) SetPartyType('Person');
  //   // eslint-disable-next-line
  // }, [isEditing]);

  const getData = async () => {
    try {
      const data = await getAddressParties(ptsAddressID);
      if (!mountedRef.current) return;
      setData(processData(data));
      setLoaded(true);
    } catch (err) {
      console.log('error ', err);
      props.handleError(err, 'Error receiving event location data.');
    }
  };

  const processData = (data) => {
    return data.map((row) => {
      return {
        ...row,
        id: row.ROWGUID,
        Name: getPartyName(row),
        PartyID: getPartyID(row),
        Relationship: row.AddressPartyRelationship,
        RelationshipDescription: row.RelationshipDescription,
      };
    });
  };

  const viewDetails = () => {
    const item = data.find((row) => row.id === selection);
    if (!item) return;
    props.showDisplayDataWindow({ data: filterData(item), title: item.Name });
  };

  const filterData = (rawData) => {
    const {
      Name,
      PartyID,
      PartyType,
      PriorityOrder,
      AdvisoryText,
      Relationship,
      RelationshipDescription,
      CreatedBy,
      Updated,
      UpdatedBy,
      IsDeleted,
      DeletedBy,
      DeletedIP,
    } = rawData;
    return {
      Name,
      PartyID,
      PartyType,
      PriorityOrder,
      AdvisoryText,
      Relationship,
      RelationshipDescription,
      CreatedBy,
      Updated,
      UpdatedBy,
      IsDeleted,
      DeletedBy,
      DeletedIP,
    };
  };

  const handleAddPartyClick = () => {
    clearData();
    setEdited(true);
    isEditing.current = false;
  };

  const clearData = () => {
    setPerson(null);
    setPlace(null);
    setPriorityOrder(0);
    setPartyRelationship(null);
    setAdvisoryText('');
    clearErr();
    setROWGUID();
    setPtsPersonID();
    setEditedPlace();
  };

  const delParty = async () => {
    const item = data.find((row) => row.id === selection);
    if (!item) return;
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    setLoaded(false);
    try {
      await delAddressParty(item.ROWGUID);
      if (!mountedRef.current) return;
      await getData();
    } catch (err) {
      props.handleError(err);
    }
  };

  const getPlace = async (ptsPlaceID) => {
    let place;
    setLoaded(false);
    try {
      const service = getService('place-lookup');
      //place = await getPlaceDetails(ptsPlaceID);
      place = await service.get(ptsPlaceID);
    } catch (err) {
      props.handleError(err);
    }
    setLoaded(true);
    return place;
  };

  const editParty = async () => {
    clearData();
    setEdited(true);
    isEditing.current = true;
    const item = data.find((row) => row.id === selection);
    if (!item) return;

    const { ptsPartyID, PartyType, Relationship, PriorityOrder, AdvisoryText, ROWGUID } = item;
    const PartyRelationship = dictionary.AddressPartyRelationship.find(
      (p) => p.Code === Relationship
    );
    if (!PartyRelationship) return;

    if (PartyType?.toLowerCase() === 'person') {
      setPtsPersonID(ptsPartyID);
      setPerson({ ptsPersonID: ptsPartyID });
    } else {
      const place = await getPlace(ptsPartyID);

      setEditedPlace(place);
      setPlace(item);
    }
    setPriorityOrder(PriorityOrder);
    setAdvisoryText(AdvisoryText);
    setROWGUID(ROWGUID);

    setPartyRelationship(PartyRelationship);
    SetPartyType(PartyType);
  };
  const renderActions = () => {
    return (
      <div className={classes.actions}>
        <Tooltip title="Add party">
          <Fab size="small" color="secondary" onClick={handleAddPartyClick}>
            <AddIcon />
          </Fab>
        </Tooltip>
        <PrintsSearch title="SOP Records" data={data} cols={columns} hiddenCols={hiddenColumns} />
        {selection !== null && (
          <>
            <Tooltip title="View details">
              <Fab size="small" color="secondary" onClick={viewDetails}>
                <InfoIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Edit party">
              <Fab size="small" color="secondary" onClick={editParty}>
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Delete party">
              <Fab size="small" color="secondary" onClick={delParty}>
                <DeleteIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const renderXGrid = () => {
    return (
      <div className={classes.gridWrap}>
        <XGrid
          columns={columns}
          rows={data}
          loading={!loaded}
          rowHeight={38}
          disableMultipleSelection={true}
          showToolbar
          disableColumnFilter
          disableSelectionOnClick={edited}
          onSelectionModelChange={(newSelection) => {
            setSelection(newSelection.selectionModel[0]);
          }}
          onColumnVisibilityChange={(col) => setHiddenColumns([...hiddenColumns, col.field])}
          getRowClassName={(params) => (params.row.ActiveWarrant ? classes.warrant : null)}
        />
      </div>
    );
  };

  const onEditClose = () => {
    clearData();
    SetPartyType('Person');
    setEdited(false);
  };

  const renderEditActions = () => {
    return (
      <div className={classes.editActions}>
        <hr />
        <Button
          color="primary"
          variant="contained"
          onClick={save}
          disabled={!isValidAndChanged() || !loaded}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={onEditClose}>
          <CloseIcon /> Close
        </Button>
      </div>
    );
  };
  const save = async () => {
    let ptsPartyID;
    if (PartyType?.toLocaleLowerCase() === 'person') {
      ptsPartyID = person.ptsPersonID;
    } else {
      ptsPartyID = place.ptsPlaceID;
    }
    const data = {
      ptsAddressID,
      ptsPartyID,
      PartyType,
      AddressPartyRelationship: PartyRelationship.Code,
      PriorityOrder: parseInt(PriorityOrder),
      AdvisoryText,
      ROWGUID,
    };
    setLoaded(false);
    try {
      await saveAddressParty(data);
      if (!mountedRef.current) return;
      onEditClose();
      await getData();
    } catch (err) {
      props.handleError(err);
    }
    isEditing.current = false;
  };

  const renderAdvisoryText = () => {
    const onChange = (ev) => {
      setAdvisoryText(ev.target.value);
      formChanged();
    };

    return (
      <TextField2
        className={classes.w100pr}
        label="Advisory Text"
        placeholder="Advisory Text"
        multiline
        value={AdvisoryText}
        onChange={onChange}
        rows={4}
        inputProps={{ style: { height: 102 } }}
      />
    );
  };

  const renderType = () => {
    const onChange = (ev) => {
      setPerson(null);
      setPlace(null);
      delErr(['Place', 'Person']);
      SetPartyType(ev.target.value);
    };

    return (
      <FormControl variant="outlined" size="small" className={classes.item}>
        <InputLabel>Party</InputLabel>
        <Select
          label="Party"
          value={PartyType?.toLocaleLowerCase()}
          onChange={onChange}
          disabled={isEditing.current}>
          <MenuItem value="person">Person</MenuItem>
          <MenuItem value="place">Place</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const renderPriority = () => {
    const onChange = (ev, val) => {
      setPriorityOrder(parseInt(val));
      formChanged();
    };

    return (
      <TextField2
        className={classes.item}
        value={PriorityOrder}
        onChange={onChange}
        min={0}
        label="Priority Order"
        type="number"
      />
    );
  };

  const renderPlaceSearch = () => {
    const error = err(place, 'Place');
    const onChange = (val) => {
      setPlace(val);
      formChanged();
    };
    return (
      <PlaceLookup
        error={error}
        ptsPlaceID={editedPlace?.ptsPlaceID}
        onChange={onChange}
        className={classes.item}
        disabled={isEditing.current}
      />
    );
  };

  const renderPersonLookup = () => {
    const onChange = (val) => {
      setPerson(val);
      setPtsPersonID();
      formChanged();
    };
    const error = !isEditing.current && err(person, 'Person');
    return (
      <PersonLookup2
        className={classes.item}
        error={error}
        onPersonChange={onChange}
        label="Search Person"
        ptsPersonID={ptsPersonID}
        disabled={isEditing.current}
      />
    );
  };

  function renderPartyRelationship() {
    const onChange = (ev, val) => {
      /* Preventing setting default value in edit mode */
      if (ev === null) {
        if (PartyRelationship === null) setPartyRelationship(val);
      } else setPartyRelationship(val);
      formChanged();
    };
    const error = err(PartyRelationship, 'party');
    return (
      <Dictionary
        className={classes.item}
        options="AddressPartyRelationship"
        onChange={onChange}
        value={PartyRelationship}
        label="Party Relationship"
        error={error}
      />
    );
  }

  const memoizedPartyRelationship = useMemo(() => renderPartyRelationship(), [
    PartyRelationship,
    classes.item,
  ]);

  const renderEditForm = () => {
    return (
      <Row>
        <ColCard minWidth={500} maxWidth={960}>
          <Row style={{ display: 'flex' }}>
            <div className={classes.col1}>
              <RowInner>
                {renderType()}
                {renderPriority()}
              </RowInner>
              <RowInner>
                {PartyType?.toLocaleLowerCase() === 'person' && renderPersonLookup()}
                {PartyType?.toLocaleLowerCase() === 'place' && renderPlaceSearch()}
                {memoizedPartyRelationship}
              </RowInner>
            </div>
            <div className={classes.col2}>
              <RowInner>{renderAdvisoryText()}</RowInner>
            </div>
          </Row>
          <div>{renderEditActions()}</div>
        </ColCard>
      </Row>
    );
  };

  return (
    <div className={classes.wrap}>
      {edited && renderEditForm()}
      {!edited && renderActions()}
      {renderXGrid()}
    </div>
  );
}

const mapStateToProps = (state) => ({
  dictionary: state.dictionary,
});

export default connect(mapStateToProps, {
  handleError,
  showDisplayDataWindow,
})(AddressParties);
